import React from 'react'
import { useQueryClinicAdminsQuery } from '~graphql/generated/graphql'
import { useUserContext } from '~config/user-context'
import { getUserFullName } from '~utils/helpers'
import MentionTextarea, {
    MentionUserOption,
} from '~components/shared/inputs/mention-textarea'

interface Props {
    value: string
    onChange: (newValue: string) => void
    placeholder: string
    clinicId: string
    onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
}

/**
 * Internal-note variant of the inbox composer textarea. Identical visual
 * weight as the plain `<InputTextarea>` but supports `@`-mentioning any
 * teammate that has access to the current chat's clinic.
 *
 * Kept as a separate component (rather than a flag on `InputTextarea`)
 * because the underlying `react-mentions` library has different layout
 * primitives than Chakra's `<Textarea>` and it'd be noisy to interleave
 * both implementations in one file.
 */
export default function InternalNoteTextarea({
    value,
    onChange,
    placeholder,
    clinicId,
    onKeyDown,
}: Props) {
    const { user } = useUserContext()

    const { data } = useQueryClinicAdminsQuery({
        variables: { clinicId },
        skip: !clinicId,
    })

    const mentionUsers: MentionUserOption[] = (data?.queryClinicAdmins || [])
        .map(admin => admin.user)
        .filter(u => u && u.id && u.id !== user?.id)
        .map(u => ({
            id: u.id,
            display: getUserFullName(u) || u.email || u.id,
            avatarUrl: u.avatar_url,
        }))

    return (
        <MentionTextarea
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            mentionUsers={mentionUsers}
            onKeyDown={onKeyDown}
        />
    )
}
